import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { FaRegHeart, FaHeart, FaRegFileImage } from "react-icons/fa6";
import { Pagination } from "antd";
import { CiCirclePlus } from "react-icons/ci";
import Moment from "react-moment";
import { useDispatch, useSelector } from "react-redux";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
    addLikePost,
    addPost,
    deletePost,
    fetchPosts,
    patchPost,
    postsSelector,
} from "../../../../service/redux/posts/postsSlice";
import {
    handleErrorImg,
    handleFormatNumber,
    slugString,
} from "../../../../service/utils/utils";
import { Auth } from "../../../../service/utils/auth";
import { usersSelector } from "../../../../service/redux/users/usersSlice";
import config from "../../../../configs/Configs.json";
import axios from "axios";
import { quillModules } from "../../../../components/shared/quillConfig";

const { LONGITUDE_DEFAULT, LATITUDE_DEFAULT, API__SERVER } = config;

// Quill editor styles
const quillEditorContentStyle = `
  .ql-container {
    background: #334155 !important;
    outline: none !important;
  }
  .ql-editor {
    color: #f1f5f9 !important;
    background: #334155 !important;
    border-radius: 0.5rem !important;
    outline: none !important;
    min-height: 150px;
    max-height: 500px;
    overflow-y: auto;
  }
  .ql-toolbar.ql-snow {
    color: #f1f5f9 !important;
    background: #1e293b !important;
    border-radius: 0.5rem 0.5rem 0 0 !important;
    border: 1px solid #475569 !important;
  }
  .ql-toolbar.ql-snow .ql-picker,
  .ql-toolbar.ql-snow .ql-picker-label,
  .ql-toolbar.ql-snow button {
    color: #f1f5f9 !important;
    fill: #f1f5f9 !important;
  }
  .ql-toolbar.ql-snow .ql-stroke {
    stroke: #f1f5f9 !important;
  }
  .ql-toolbar.ql-snow .ql-fill {
    fill: #f1f5f9 !important;
  }
  .ql-container.ql-snow {
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
    border: 1px solid #475569;
  }
`;

function PostsNew() {
    const { userID } = new Auth();
    const { user } = useSelector(usersSelector);
    const dispatch = useDispatch();
    const { groupId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Number(searchParams.get("page")) || 1;

    // States
    const [imageBase64s, setImageBase64s] = useState([]);
    const [imageFiles, setImageFiles] = useState([]);
    const [isShowFormCreate, setIsShowFormCreate] = useState(false);
    const [isShowFormUpdate, setIsShowFormUpdate] = useState(false);
    const [dataPost, setDataPost] = useState({ Title: "", Content: "" });
    const [dataUpdatePost, setDataUpdatePost] = useState({});
    const [latestComments, setLatestComments] = useState({});
    const [groupName, setGroupName] = useState("");

    const { posts, changeTogglePosts, totalPage, totalPost } = useSelector(postsSelector);

    // Debug pagination data
    useEffect(() => {
        console.log("📊 Pagination Debug:", { totalPage, totalPost, postsLength: posts?.length });
    }, [totalPage, totalPost, posts]);

    // Fetch posts
    useEffect(() => {
        if (groupId && userID) {
            const limit = 10;
            dispatch(fetchPosts({ groupId, userID, page, limit }));
        }
    }, [groupId, userID, page, changeTogglePosts, dispatch]);

    // Fetch group name
    useEffect(() => {
        if (groupId) {
            axios
                .get(`https://iudi-api.thinkdiff.us/api/detail_group/${groupId}/1`)
                .then((res) => {
                    setGroupName(res.data?.detailGroup?.GroupName || "");
                })
                .catch(() => setGroupName(""));
        }
    }, [groupId]);

    // Fetch latest comments for each post
    useEffect(() => {
        async function fetchLatestComments() {
            const comments = {};
            for (const post of posts) {
                try {
                    const { data } = await axios.get(
                        `${API__SERVER}/forum/comment/${post.PostID}/${userID}/10?page=1`
                    );
                    const reversedComments = Array.isArray(data?.Comments)
                        ? [...data.Comments].reverse()
                        : [];
                    comments[post.PostID] = reversedComments.slice(0, 3);
                } catch (error) {
                    comments[post.PostID] = [];
                }
            }
            setLatestComments(comments);
        }

        if (posts.length > 0) {
            fetchLatestComments();
        }
    }, [posts, userID]);

    // Auto set page=1
    useEffect(() => {
        if (!searchParams.get("page")) {
            setSearchParams({ page: 1 });
        }
    }, [groupId]);

    // Handlers
    const handleDeletePost = (postID) => {
        if (window.confirm("Bạn có chắc chắn muốn xóa bài viết này?")) {
            dispatch(deletePost(postID));
        }
    };

    const handlePushImage = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
            setImageBase64s((prev) => [...prev, reader.result]);
            setImageFiles((prev) => [...prev, file]);
        };
    };

    const handleDeleteListImage = (index) => {
        setImageBase64s((prev) => prev.filter((_, i) => i !== index));
        setImageFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleChangePage = (newPage) => {
        setSearchParams({ page: newPage });
    };

    const handleSubmitCreatePost = () => {
        if (!dataPost.Title?.trim() || !dataPost.Content?.trim()) {
            alert("Vui lòng nhập đầy đủ tiêu đề và nội dung!");
            return;
        }

        const formData = new FormData();
        formData.append("Content", dataPost.Content);
        formData.append("Title", dataPost.Title);
        formData.append("GroupID", groupId);
        formData.append("PostLatitude", LATITUDE_DEFAULT);
        formData.append("PostLongitude", LONGITUDE_DEFAULT);
        imageFiles.forEach((file) => formData.append("PhotoURL", file));

        dispatch(addPost({ data: formData, userID }));
        setDataPost({ Title: "", Content: "" });
        setImageBase64s([]);
        setImageFiles([]);
        setIsShowFormCreate(false);
    };

    const handleSubmitUpdatePost = () => {
        if (!dataUpdatePost.Title?.trim() || !dataUpdatePost.Content?.trim()) {
            alert("Vui lòng nhập đầy đủ tiêu đề và nội dung!");
            return;
        }

        const formData = new FormData();
        formData.append("Content", dataUpdatePost.Content);
        formData.append("Title", dataUpdatePost.Title);
        formData.append("GroupID", groupId);
        formData.append("PostLatitude", LATITUDE_DEFAULT);
        formData.append("PostLongitude", LONGITUDE_DEFAULT);
        imageFiles.forEach((file) => formData.append("PhotoURL", file));

        dispatch(patchPost({ data: formData, postID: dataUpdatePost.PostID }));
        setDataUpdatePost({});
        setImageBase64s([]);
        setImageFiles([]);
        setIsShowFormUpdate(false);
    };

    const handleEditPost = (post) => {
        setDataUpdatePost({
            PostID: post.PostID,
            Title: post.Title,
            Content: post.Content,
        });
        setIsShowFormUpdate(true);
    };

    const formatNumber = (num) => {
        if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'k';
        }
        return num?.toString() || '0';
    };

    return (
        <div className="posts-new-container">
            {/* Header với nút tạo bài viết */}
            <div className="posts-header">
                <div className="posts-nav">
                    <button className="nav-btn active">📰 Bài viết mới</button>
                    <button className="nav-btn">🔔 Chưa đọc</button>
                </div>
                <button
                    className="create-post-btn"
                    onClick={() => setIsShowFormCreate(true)}
                >
                    <CiCirclePlus size={20} />
                    Tạo bài viết
                </button>
            </div>

            {/* Danh sách Posts */}
            <div className="posts-list">
                {posts && posts.length > 0 ? (
                    posts.map((post) => (
                        <div key={post.PostID} className="post-card">
                            {/* Post Header */}
                            <div className="post-header">
                                <div className="post-author">
                                    <img
                                        src={post.Avatar}
                                        alt={post.UserFullName}
                                        className="author-avatar"
                                        onError={(e) => handleErrorImg(e.target)}
                                    />
                                    <div className="author-info">
                                        <span className="author-name">{post.UserFullName || post.Username}</span>
                                        <span className="post-time">
                                            <Moment fromNow>{`${post.UpdatePostAt}+0700`}</Moment>
                                        </span>
                                    </div>
                                </div>
                                {post.UserID === userID && (
                                    <div className="post-actions">
                                        <button
                                            className="action-btn edit-btn"
                                            onClick={() => handleEditPost(post)}
                                        >
                                            ✏️ Sửa
                                        </button>
                                        <button
                                            className="action-btn delete-btn"
                                            onClick={() => handleDeletePost(post.PostID)}
                                        >
                                            🗑️ Xóa
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Post Content */}
                            <Link
                                to={`/group/${slugString(groupName || "group")}/${groupId}/threads/${slugString(post.Title || "post")}/${post.PostID}`}
                                className="post-content-link"
                                onClick={(e) => {
                                    console.log("🔗 Click post:", {
                                        postId: post.PostID,
                                        postTitle: post.Title,
                                        groupId,
                                        groupName,
                                        linkTo: `/group/${slugString(groupName || "group")}/${groupId}/threads/${slugString(post.Title || "post")}/${post.PostID}`
                                    });
                                }}
                            >
                                <div className="post-content">
                                    <h3 className="post-title">{post.Title}</h3>
                                    <div
                                        className="post-text"
                                        dangerouslySetInnerHTML={{
                                            __html: post.Content?.substring(0, 300) + (post.Content?.length > 300 ? "..." : ""),
                                        }}
                                    />
                                    {post.Photo && post.Photo.length > 0 && (
                                        <div className="post-images">
                                            <img
                                                src={post.Photo[0]}
                                                alt="Post"
                                                className="post-image"
                                                onError={(e) => handleErrorImg(e.target)}
                                            />
                                        </div>
                                    )}
                                </div>
                            </Link>

                            {/* Post Footer với stats */}
                            <div className="post-footer">
                                <div className="post-stats">
                                    <span className="stat-item">
                                        <span className="stat-icon">👁️</span>
                                        {formatNumber(post.ViewCount || 0)}
                                    </span>
                                    <span className="stat-item">
                                        <span className="stat-icon">💬</span>
                                        {latestComments[post.PostID]?.length || 0}
                                    </span>
                                    <button
                                        className="stat-item like-btn"
                                        onClick={() =>
                                            dispatch(addLikePost({ postId: post.PostID, userID }))
                                        }
                                    >
                                        {post.favoriteType ? (
                                            <FaHeart className="stat-icon text-red-500" />
                                        ) : (
                                            <FaRegHeart className="stat-icon" />
                                        )}
                                        {formatNumber(post.FavoriteCount || 0)}
                                    </button>
                                </div>
                            </div>

                            {/* Latest Comments Preview */}
                            {latestComments[post.PostID] && latestComments[post.PostID].length > 0 && (
                                <div className="comments-preview">
                                    <div className="comments-header">
                                        <span>💬 {latestComments[post.PostID].length} bình luận gần đây</span>
                                    </div>
                                    {latestComments[post.PostID].map((comment, idx) => (
                                        <div key={idx} className="comment-item">
                                            <img
                                                src={comment.Avatar}
                                                alt={comment.FullName}
                                                className="comment-avatar"
                                                onError={(e) => handleErrorImg(e.target)}
                                            />
                                            <div className="comment-content">
                                                <span className="comment-author">{comment.FullName}</span>
                                                <div
                                                    className="comment-text"
                                                    dangerouslySetInnerHTML={{
                                                        __html: comment.Content?.substring(0, 100) + (comment.Content?.length > 100 ? "..." : ""),
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))
                ) : (
                    <div className="no-posts">
                        <div className="no-posts-icon">📝</div>
                        <p>Chưa có bài viết nào trong nhóm này</p>
                        <button
                            className="create-first-post-btn"
                            onClick={() => setIsShowFormCreate(true)}
                        >
                            Tạo bài viết đầu tiên
                        </button>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {(totalPage > 1 || totalPost > 10) && (
                <div className="pagination-container">
                    <Pagination
                        total={totalPost || 0}
                        pageSize={10}
                        current={page}
                        onChange={handleChangePage}
                        showSizeChanger={false}
                        className="custom-pagination"
                    />
                </div>
            )}

            {/* Modal Create Post */}
            {isShowFormCreate && (
                <div className="modal-overlay" onClick={() => setIsShowFormCreate(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>✏️ Tạo bài viết mới</h3>
                            <button
                                className="close-btn"
                                onClick={() => setIsShowFormCreate(false)}
                            >
                                ✕
                            </button>
                        </div>
                        <div className="modal-body">
                            <input
                                type="text"
                                placeholder="Tiêu đề bài viết..."
                                value={dataPost.Title}
                                onChange={(e) =>
                                    setDataPost((prev) => ({ ...prev, Title: e.target.value }))
                                }
                                className="title-input"
                            />
                            <ReactQuill
                                theme="snow"
                                value={dataPost.Content}
                                onChange={(value) =>
                                    setDataPost((prev) => ({ ...prev, Content: value }))
                                }
                                modules={quillModules}
                                placeholder="Nội dung bài viết..."
                                style={{ marginBottom: "16px" }}
                            />
                            {/* Image Preview */}
                            {imageBase64s.length > 0 && (
                                <div className="images-preview">
                                    {imageBase64s.map((img, idx) => (
                                        <div key={idx} className="preview-item">
                                            <img src={img} alt="Preview" />
                                            <button
                                                className="remove-img-btn"
                                                onClick={() => handleDeleteListImage(idx)}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="modal-actions">
                                <label className="upload-btn">
                                    <FaRegFileImage size={18} />
                                    Thêm ảnh
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handlePushImage}
                                        style={{ display: "none" }}
                                    />
                                </label>
                                <button
                                    className="submit-btn"
                                    onClick={handleSubmitCreatePost}
                                >
                                    Đăng bài
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Update Post */}
            {isShowFormUpdate && (
                <div className="modal-overlay" onClick={() => setIsShowFormUpdate(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>✏️ Chỉnh sửa bài viết</h3>
                            <button
                                className="close-btn"
                                onClick={() => setIsShowFormUpdate(false)}
                            >
                                ✕
                            </button>
                        </div>
                        <div className="modal-body">
                            <input
                                type="text"
                                placeholder="Tiêu đề bài viết..."
                                value={dataUpdatePost.Title}
                                onChange={(e) =>
                                    setDataUpdatePost((prev) => ({ ...prev, Title: e.target.value }))
                                }
                                className="title-input"
                            />
                            <ReactQuill
                                theme="snow"
                                value={dataUpdatePost.Content}
                                onChange={(value) =>
                                    setDataUpdatePost((prev) => ({ ...prev, Content: value }))
                                }
                                modules={quillModules}
                                placeholder="Nội dung bài viết..."
                                style={{ marginBottom: "16px" }}
                            />
                            <div className="modal-actions">
                                <label className="upload-btn">
                                    <FaRegFileImage size={18} />
                                    Thêm ảnh
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handlePushImage}
                                        style={{ display: "none" }}
                                    />
                                </label>
                                <button
                                    className="submit-btn"
                                    onClick={handleSubmitUpdatePost}
                                >
                                    Cập nhật
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* CSS Styles */}
            <style>{quillEditorContentStyle}</style>
            <style jsx>{`
        .posts-new-container {
          background: #1e293b;
          border-radius: 16px;
          padding: 24px;
          border: 1px solid #334155;
          min-height: 600px;
        }

        /* Header */
        .posts-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px solid #334155;
        }

        .posts-nav {
          display: flex;
          gap: 8px;
        }

        .nav-btn {
          background: transparent;
          color: #cbd5e1;
          border: 1px solid #475569;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .nav-btn:hover {
          border-color: #4ade80;
          color: #4ade80;
        }

        .nav-btn.active {
          background: #4ade80;
          color: #0f172a;
          border-color: #4ade80;
        }

        .create-post-btn {
          background: #4ade80;
          color: #0f172a;
          border: none;
          padding: 12px 20px;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
        }

        .create-post-btn:hover {
          background: #22c55e;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(74, 222, 128, 0.3);
        }

        /* Posts List */
        .posts-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 24px;
        }

        .post-card {
          background: #334155;
          border-radius: 12px;
          padding: 20px;
          border: 1px solid #475569;
          transition: all 0.3s ease;
        }

        .post-card:hover {
          border-color: #4ade80;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
        }

        .post-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .post-author {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .author-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          object-fit: cover;
        }

        .author-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .author-name {
          font-size: 14px;
          font-weight: 600;
          color: #f1f5f9;
        }

        .post-time {
          font-size: 12px;
          color: #94a3b8;
        }

        .post-actions {
          display: flex;
          gap: 8px;
        }

        .action-btn {
          padding: 6px 12px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.2s ease;
        }

        .edit-btn {
          background: #3b82f6;
          color: white;
        }

        .edit-btn:hover {
          background: #2563eb;
        }

        .delete-btn {
          background: #ef4444;
          color: white;
        }

        .delete-btn:hover {
          background: #dc2626;
        }

        .post-content-link {
          text-decoration: none;
          color: inherit;
          display: block;
        }

        .post-content {
          margin-bottom: 16px;
        }

        .post-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: #f1f5f9;
          margin-bottom: 8px;
          line-height: 1.4;
        }

        .post-text {
          color: #cbd5e1;
          font-size: 14px;
          line-height: 1.5;
          margin-bottom: 12px;
        }

        .post-images {
          margin-top: 12px;
        }

        .post-image {
          width: 100%;
          max-width: 400px;
          height: 200px;
          object-fit: cover;
          border-radius: 8px;
        }

        .post-footer {
          padding-top: 12px;
          border-top: 1px solid #475569;
        }

        .post-stats {
          display: flex;
          gap: 16px;
          align-items: center;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 4px;
          color: #94a3b8;
          font-size: 12px;
          font-weight: 500;
        }

        .stat-icon {
          font-size: 14px;
        }

        .like-btn {
          background: transparent;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .like-btn:hover {
          color: #4ade80;
        }

        /* Comments Preview */
        .comments-preview {
          margin-top: 16px;
          padding-top: 12px;
          border-top: 1px solid #475569;
        }

        .comments-header {
          font-size: 13px;
          color: #cbd5e1;
          margin-bottom: 12px;
          font-weight: 500;
        }

        .comment-item {
          display: flex;
          gap: 10px;
          margin-bottom: 10px;
          padding: 8px;
          background: #1e293b;
          border-radius: 8px;
        }

        .comment-avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          object-fit: cover;
        }

        .comment-content {
          flex: 1;
        }

        .comment-author {
          font-size: 12px;
          font-weight: 600;
          color: #f1f5f9;
          display: block;
          margin-bottom: 4px;
        }

        .comment-text {
          font-size: 12px;
          color: #cbd5e1;
          line-height: 1.4;
        }

        /* No Posts */
        .no-posts {
          text-align: center;
          padding: 60px 20px;
          color: #94a3b8;
        }

        .no-posts-icon {
          font-size: 4rem;
          margin-bottom: 16px;
          opacity: 0.6;
        }

        .create-first-post-btn {
          background: #4ade80;
          color: #0f172a;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          margin-top: 16px;
        }

        .create-first-post-btn:hover {
          background: #22c55e;
        }

        /* Pagination */
        .pagination-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 32px auto;
          padding: 24px 0;
          background: rgba(30, 41, 59, 0.6);
          border-radius: 12px;
          backdrop-filter: blur(10px);
        }

        .custom-pagination .ant-pagination-item {
          background: #334155 !important;
          border: 1px solid #475569 !important;
          border-radius: 8px !important;
        }

        .custom-pagination .ant-pagination-item a {
          color: #cbd5e1 !important;
        }

        .custom-pagination .ant-pagination-item-active {
          background: #22c55e !important;
          border-color: #22c55e !important;
        }

        .custom-pagination .ant-pagination-item-active a {
          color: #ffffff !important;
        }

        .custom-pagination .ant-pagination-prev,
        .custom-pagination .ant-pagination-next {
          background: #334155 !important;
          border: 1px solid #475569 !important;
          border-radius: 8px !important;
        }

        .custom-pagination .ant-pagination-prev button,
        .custom-pagination .ant-pagination-next button {
          color: #cbd5e1 !important;
        }

        .custom-pagination .ant-pagination-item:hover,
        .custom-pagination .ant-pagination-prev:hover,
        .custom-pagination .ant-pagination-next:hover {
          border-color: #22c55e !important;
          background: rgba(34, 197, 94, 0.2) !important;
        }

        .custom-pagination .ant-pagination-disabled {
          opacity: 0.5;
          pointer-events: none;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background: #1e293b;
          border-radius: 16px;
          width: 90%;
          max-width: 700px;
          max-height: 90vh;
          overflow-y: auto;
          border: 1px solid #334155;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #334155;
        }

        .modal-header h3 {
          color: #f1f5f9;
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0;
        }

        .close-btn {
          background: transparent;
          border: none;
          color: #cbd5e1;
          font-size: 24px;
          cursor: pointer;
          transition: color 0.2s;
        }

        .close-btn:hover {
          color: #ef4444;
        }

        .modal-body {
          padding: 20px;
        }

        .title-input {
          width: 100%;
          padding: 12px 16px;
          background: #334155;
          border: 1px solid #475569;
          border-radius: 8px;
          color: #f1f5f9;
          font-size: 16px;
          margin-bottom: 16px;
          transition: all 0.2s ease;
        }

        .title-input:focus {
          outline: none;
          border-color: #4ade80;
        }

        .images-preview {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
          gap: 10px;
          margin-bottom: 16px;
        }

        .preview-item {
          position: relative;
        }

        .preview-item img {
          width: 100%;
          height: 100px;
          object-fit: cover;
          border-radius: 8px;
        }

        .remove-img-btn {
          position: absolute;
          top: 4px;
          right: 4px;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          cursor: pointer;
          font-size: 12px;
        }

        .modal-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .upload-btn {
          background: #334155;
          color: #f1f5f9;
          border: 1px solid #475569;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s ease;
        }

        .upload-btn:hover {
          background: #475569;
        }

        .submit-btn {
          background: #4ade80;
          color: #0f172a;
          border: none;
          padding: 10px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .submit-btn:hover {
          background: #22c55e;
        }

        @media (max-width: 768px) {
          .posts-new-container {
            padding: 16px;
          }

          .posts-header {
            flex-direction: column;
            gap: 12px;
            align-items: stretch;
          }

          .posts-nav {
            justify-content: center;
          }

          .create-post-btn {
            justify-content: center;
          }
        }
      `}</style>
        </div>
    );
}

export default PostsNew;

